import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import type { HeroManager } from './types';

interface ManagerMapProps {
    manager: HeroManager;
    title: string;
}

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

/**
 * Street-level map of a manager's exact point (Leaflet + OpenStreetMap tiles,
 * same stack as the /branch agents map). Leaflet is loaded on first open only.
 */
export function ManagerMap({ manager, title }: ManagerMapProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let disposed = false;
        let cleanup: (() => void) | undefined;

        import('leaflet').then((leaflet) => {
            const L = leaflet.default;
            const container = containerRef.current;

            if (disposed || !container) {
                return;
            }

            const map = L.map(container, { zoomControl: true, scrollWheelZoom: false, attributionControl: true }).setView([manager.lat, manager.lng], 14);
            L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(map);

            const face = manager.avatar
                ? `<img src="${escapeHtml(manager.avatar)}" alt="">`
                : `<span>${escapeHtml(manager.initials)}</span>`;
            const icon = L.divIcon({
                className: 'wop-map-pin',
                html: `<span class="wop-map-pin__face">${face}</span>`,
                iconSize: [46, 58],
                iconAnchor: [23, 58],
            });
            L.marker([manager.lat, manager.lng], { icon, title: manager.name }).addTo(map);

            cleanup = () => map.remove();
        });

        return () => {
            disposed = true;
            cleanup?.();
        };
    }, [manager]);

    return <div ref={containerRef} className="wop-manager-card__leaflet" role="region" aria-label={title} />;
}
